import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import useAdminPro from "app/universal/route/useAdminPro";
import { Alert, Badge, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getSavEnCours, getSavConstants, getClientSavHistory } from "state/sav/actions";
import { MOTIF_LABELS, STATUT_LABELS, labelFor } from "app/orderTool/pages/pipelineSav/savLabels";
import ClientFicheModal from "app/orderTool/components/ClientFicheModal";
import SavDetailModal from "app/orderTool/pages/pipelineSav/SavDetailModal";

/** Un SAV est « récent » pendant 6 mois après l'incident. */
const RECENT_MOIS = 6;

const estRecent = (sav) => {
  const d = sav?.date_incident || sav?.created_at;
  if (!d) return false;
  const limite = new Date();
  limite.setMonth(limite.getMonth() - RECENT_MOIS);
  return new Date(d) >= limite;
};

/**
 * Bannière SAV sur une fiche devis. Deux cas (retour Camille 17/09) :
 *  - SAV non réglé en cours → « SAV en cours (N) » ;
 *  - plus aucun SAV en cours mais un historique récent → « Historique de SAV
 *    récent (N) », pour que le chargé de clientèle qui reprend une commande
 *    voie tout de suite le passif du client.
 * Dans les deux cas le dernier SAV s'ouvre au clic, et la fiche client donne
 * accès à l'historique complet.
 */
const SavEnCoursBanner = ({ clientId, entrepriseId }) => {
  // Bandeau SAV : réservé aux admins pro, comme l'onglet SAV B2B.
  const canSav = useAdminPro();
  const dispatch = useDispatch();
  const enCours = useSelector((state) => state.sav.enCours);
  const constants = useSelector((state) => state.sav.constants);
  const [ficheOpen, setFicheOpen] = useState(false);
  const [savId, setSavId] = useState(null);
  const [historique, setHistorique] = useState([]);

  const reload = () => {
    if (clientId || entrepriseId) {
      dispatch(getSavEnCours(clientId, entrepriseId));
      dispatch(getClientSavHistory({ clientId, entrepriseId })).then((resp) => {
        setHistorique((resp?.savs || []).filter(Boolean));
      });
    }
  };

  useEffect(() => {
    if (!canSav) {
      return;
    }
    reload();
    if (!constants) {
      dispatch(getSavConstants());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, entrepriseId, canSav, dispatch]);

  if (!canSav) {
    return null;
  }

  // Sans SAV en cours, on retombe sur l'historique récent — même bandeau jaune,
  // libellé différent.
  const recents = historique.filter(estRecent);
  const aDesEnCours = !!enCours?.count;
  if (!aDesEnCours && recents.length === 0) {
    return null;
  }

  const liste = aDesEnCours ? enCours.savs : recents;
  const dernier = liste[0];
  // L'alerte dit clairement si le SAV est réglé ou non (retour terrain Keep, 26/10/2026).
  const titre = aDesEnCours
    ? `SAV en cours, non réglé (${enCours.count})`
    : `Historique de SAV récent, tout est réglé (${recents.length})`;
  const statutDernier = dernier?.statut;
  const estRegle = statutDernier === "REGLE";

  return (
    <>
      <Alert variant="warning" className="mb-0">
        <strong>{titre}</strong>
        {dernier && (
          <>
            {" — "}
            <Button
              variant="link"
              size="sm"
              className="p-0 align-baseline"
              onClick={() => setSavId(dernier.id)}
            >
              {dernier.reference}
            </Button>
            {" : "}
            {labelFor(MOTIF_LABELS, dernier.motif)}
            {dernier.commande_groupee_numero ? ` (${dernier.commande_groupee_numero})` : ""}
            {statutDernier && (
              <Badge variant={estRegle ? "success" : "danger"} className="ml-1">
                {estRegle ? "Réglé" : `Non réglé — ${labelFor(STATUT_LABELS, statutDernier)}`}
              </Badge>
            )}
          </>
        )}
        {" "}
        {(clientId || entrepriseId) && (
          <>
            <Button variant="link" size="sm" className="p-0 align-baseline" onClick={() => setFicheOpen(true)}>
              Fiche client
            </Button>
            {" · "}
          </>
        )}
        <Link to="/pipeline-sav">Voir le pipeline SAV</Link>
      </Alert>

      <ClientFicheModal
        show={ficheOpen}
        clientId={clientId}
        entrepriseId={entrepriseId}
        onClose={() => setFicheOpen(false)}
        onOpenSav={(id) => setSavId(id)}
      />

      <SavDetailModal
        show={savId !== null}
        savId={savId}
        constants={constants}
        onClose={() => setSavId(null)}
        onSaved={reload}
      />
    </>
  );
};

export default SavEnCoursBanner;
